import React from "react";
import PageFrame from "./PageFrame";
import {formatPhoneNumber} from "../../../utils";

/**
 * Page 02 - Offering Summary + map on the left, Income & Expenses on the right.
 *
 * Consumes the same table shapes the existing deck builds in
 * ../../PrintMultiTenant/tables.js, so the underwriting extraction logic is shared
 * rather than duplicated. Those builders serve the old deck too, so the rows this
 * template does not print are filtered out here rather than removed at source.
 */

/**
 * Offering summary rows the approved template omits, leaving every multi-tenant deck printing
 * the same six:
 *
 *   Price, Cap Rate, PSF, Net Operating Income, Year Built, Gross Leasable Area
 *
 * Retail printed Lot Size instead of PSF until the business asked for the office layout across
 * the board. That does diverge from the older retail multi-tenant reference PDF, which shows Lot
 * Size and no PSF; the newer sheets are the ones being followed.
 */
const SUMMARY_DROP = ['occupancy', 'acres'];

/**
 * The builder labels the row "Price PSF". The reference heads it "PSF", beside a CURRENT/PSF
 * column pair that uses the same short form.
 */
const PSF_LABEL = 'PSF';

/**
 * Income & Expenses rows the approved template omits. "Base Rent -" is the untitled
 * third base-rent row the source emits, which prints as a stray label with no subject.
 */
const EXPENSE_DROP = [
    'base rent - lease up space',
    'base rent -',
    'vacancy factor (economic)',
];

/**
 * The source abbreviates these two; every reference spells them out. Relabelled here rather than
 * at source because the builders in ../../PrintMultiTenant/tables.js also feed the older portrait
 * deck, which is laid out for the short form.
 */
const EXPENSE_RELABEL = {
    'mgmt. fee': 'Management Fee',
    'mgmt. fee (% of gpi)': 'Management Fee (% of GPI)',
};

function key(label) {
    return String(label == null ? '' : label).trim().toLowerCase();
}

/**
 * Moves the PSF row below Cap Rate, where the reference puts it.
 *
 * Placed after the *first* Cap Rate row rather than at a fixed index, because a proforma deal
 * emits "Cap Rate (As Is)" and "Net Operating Income (As Is)" followed by a second pair labelled
 * "(Proforma)". The reference does not show a proforma property, so this keeps PSF with the As Is
 * figures and leaves the proforma pair together below.
 */
function withPsfBelowCapRate(rows) {
    const psf = rows.filter(row => key(row.label) === 'price psf')
        .map(row => ({...row, label: PSF_LABEL}))[0];
    if (!psf) return rows;

    const rest = rows.filter(row => key(row.label) !== 'price psf');
    const capRate = rest.map(row => key(row.label).indexOf('cap rate') === 0).indexOf(true);
    if (capRate === -1) return rest.concat(psf);

    return rest.slice(0, capRate + 1).concat(psf, rest.slice(capRate + 1));
}

/** Drops the omitted rows and moves PSF into the position the reference gives it. */
function summaryRows(rows) {
    return withPsfBelowCapRate(
        (rows || []).filter(row => SUMMARY_DROP.indexOf(key(row.label)) === -1)
    );
}

function expenseRows(rows) {
    return (rows || [])
        .filter(row => {
            const cells = row.valueCells || [];
            return EXPENSE_DROP.indexOf(key(cells[0] && cells[0].value)) === -1;
        })
        .map(row => {
            const cells = row.valueCells || [];
            const relabel = EXPENSE_RELABEL[key(cells[0] && cells[0].value)];
            if (!relabel) return row;
            return {
                ...row,
                valueCells: [{...cells[0], value: relabel}].concat(cells.slice(1)),
            };
        });
}

export default function OfferingSummary(props) {
    const {
        property,
        subProduct,
        offeringSummary,
        incomeAndExpenses,
        agents,
        mapImageUrl,
        pageNo,
        totalPages,
    } = props;

    const headerRight = [property.address, property.city, property.state, property.zip]
        .filter(Boolean)
        .join(', ');

    const rows = summaryRows(offeringSummary.rows);
    const expenses = expenseRows(incomeAndExpenses.rows);

    return (
        <PageFrame
            eyebrow={`OFFERING SUMMARY  •  ${subProduct}`}
            title={firstWord(property.name)}
            titleAccent={restOfName(property.name)}
            headerRight={headerRight}
            pageNo={pageNo}
            totalPages={totalPages}
        >
            <div className='bov-two-col'>
                <div className='bov-two-col__left'>
                    <div className={`bov-card bov-card--summary${rows.length > 6 ? ' bov-card--dense' : ''}`}>
                        <div className='bov-card__title'>{offeringSummary.name}</div>
                        {rows.map((row, i) => (
                            <div key={i} className='bov-kv'>
                                <div className='bov-kv__label'>{row.label}</div>
                                <div className={`bov-kv__value${i === 0 ? ' bov-kv__value--accent' : ''}`}>
                                    {row.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='bov-map'>
                        {mapImageUrl
                            ? <img className='bov-map__img' src={mapImageUrl} alt=''/>
                            : null}
                    </div>

                    <AgentLine agents={agents} index={0}/>
                </div>

                <div className='bov-two-col__right'>
                    <div className='bov-card bov-card--ie'>
                        <div className='bov-ie'>
                            <div className='bov-ie__row bov-ie__row--head'>
                                <div className='bov-ie__label'>{incomeAndExpenses.header[0]}</div>
                                <div className='bov-ie__note'>{incomeAndExpenses.header[1]}</div>
                                <div className='bov-ie__num'>{incomeAndExpenses.header[2]}</div>
                                <div className='bov-ie__num'>{incomeAndExpenses.header[3]}</div>
                            </div>

                            {expenses.map((row, i) => {
                                const cells = row.valueCells || [];
                                const label = cells[0] ? cells[0].value : '';
                                return (
                                    <div
                                        key={i}
                                        className={`bov-ie__row${isEmphasised(label) ? ' bov-ie__row--strong' : ''}`}
                                    >
                                        <div className='bov-ie__label'>{label}</div>
                                        <div className='bov-ie__note'>{cells[1] ? cells[1].value : ''}</div>
                                        <div className='bov-ie__num'>{cells[2] ? cells[2].value : ''}</div>
                                        <div className='bov-ie__num'>{cells[3] ? cells[3].value : ''}</div>
                                    </div>
                                );
                            })}

                            {incomeAndExpenses.footer ? (
                                <div className='bov-ie__row bov-ie__row--total'>
                                    <div className='bov-ie__label'>{incomeAndExpenses.footer[0]}</div>
                                    <div className='bov-ie__note'>{incomeAndExpenses.footer[1]}</div>
                                    <div className='bov-ie__num'>{incomeAndExpenses.footer[2]}</div>
                                    <div className='bov-ie__num'>{incomeAndExpenses.footer[3]}</div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <AgentLine agents={agents} index={1}/>
                </div>
            </div>
        </PageFrame>
    );
}

export function AgentLine({agents, index}) {
    const agent = (agents || [])[index];
    if (!agent) return <div className='bov-agent'/>;
    return (
        <div className='bov-agent'>
            {[agent.name, formatPhoneNumber(agent.phone)].filter(Boolean).join(' ')}
        </div>
    );
}

/**
 * Rows the design sets in bold - section headings and running totals. The table data
 * carries no emphasis flag, so it is derived from the label.
 */
const EMPHASISED = [
    'expense reimbursements',
    'total expense reimbursements',
    'operating expenses',
];

function isEmphasised(label) {
    if (!label) return false;
    const trimmed = String(label).trim();
    if (EMPHASISED.indexOf(trimmed.toLowerCase()) !== -1) return true;
    // Running totals are written in caps in the source data.
    const letters = trimmed.replace(/[^A-Za-z]/g, '');
    return letters.length > 3 && letters === letters.toUpperCase();
}

export function firstWord(name) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/);
    return parts.length > 1 ? `${parts[0]} ` : '';
}

export function restOfName(name) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/);
    const rest = parts.length > 1 ? parts.slice(1).join(' ') : parts[0];
    return rest ? `${rest}.` : '';
}
